import { RoleKey, Title, UserRef } from '../models/access.models';

export const AVATAR_COLORS: readonly string[] = ['#bfdbfe', '#fde68a', '#bbf7d0', '#e9d5ff', '#fecaca', '#c7d2fe'];

/** «Юлия Япиева» → «ЮЯ»: the first letters of the first two words. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(w => w.length > 0);
  return words.slice(0, 2).map(w => w.charAt(0).toUpperCase()).join('');
}

export function avatarColor(id: number): string {
  return AVATAR_COLORS[Math.abs(id) % AVATAR_COLORS.length];
}

/** The title in the language of the page; the Russian one is the one that always exists. */
export function titleIn(title: Title | null | undefined, lang: string): string {
  if (!title) {
    return '';
  }
  if (lang === 'en') {
    return title.en ?? title.ru;
  }
  if (lang === 'kk') {
    return title.kk ?? title.ru;
  }
  return title.ru;
}

/** The index of the word form for the count: 0 — «1 сотрудник», 1 — «2 сотрудника», 2 — «5 сотрудников». */
export function pluralIndex(n: number, lang: string): number {
  if (lang === 'ru') {
    const mod10 = Math.abs(n) % 10;
    const mod100 = Math.abs(n) % 100;
    if (mod10 === 1 && mod100 !== 11) {
      return 0;
    }
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
      return 1;
    }
    return 2;
  }
  if (lang === 'kk') {
    return 0;
  }
  return n === 1 ? 0 : 1;
}

/** Picks the word form out of a translation like «сотрудник|сотрудника|сотрудников». */
export function pluralForm(forms: string, n: number, lang: string): string {
  const parts = forms.split('|');
  const index = pluralIndex(n, lang);
  return parts[Math.min(index, parts.length - 1)];
}

const THOUSANDS = /\B(?=(\d{3})+(?!\d))/g;

/** 1104 → «1 104» (a non-breaking space, as on the boards). */
export function spaced(n: number): string {
  return String(n).replace(THOUSANDS, ' ');
}

/** The roles in the order of strength of the contract (§9): which of several memberships speaks for a person. */
export const ROLE_ORDER: readonly RoleKey[] = ['HEAD', 'STORE_MANAGER', 'MANAGER', 'CC_OPERATOR', 'CONFECTIONER', 'SELLER', 'CASHIER_KIOSK', 'COURIER', 'MIXED', 'PROFILE_ONLY'];

export function phoneOf(user: UserRef): string {
  const digits = user.phone.replace(/\D/g, '');
  if (digits.length === 11) {
    return `+${digits[0]} ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9)}`;
  }
  return user.phone.startsWith('+') ? user.phone : `+${user.phone}`;
}

/** Local day of an ISO moment as `yyyy-MM-dd`, for grouping the journal by day. */
export function dayKey(iso: string): string {
  const d = new Date(iso);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}
